import { FC, lazy } from "react";
import { Link, Route, Switch } from "react-router-dom";
import * as _ from "lodash";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Button, PlusCircleOutlined, Grid } from "@ctra/components";
import { GamePlanEntity, Breadcrumbs } from "@ctra/api";
import { GAContext, useGoogleAnalytics } from "@ctra/analytics";

import { Routes } from "@routes";
import { useFarm } from "@farms";

import { Onboarding } from "../../onboarding";
import { GAActions, GACategories } from "../../analytics";
import { GamePlanContext, GamePlanList, GamePlanVariant } from "../GamePlanList";
import styles from "./GamePlansPage.module.less";

const EditGamePlan = lazy(() => import("../EditGamePlan"));
const CreateGamePlan = lazy(() => import("../CreateGamePlan"));

/**
 * Game plans page
 * @constructor
 */
const GamePlansPage: FC = () => {
  const { t } = useTranslation();
  const routes = Routes.app.gamePlans;
  const { trackEvent } = useGoogleAnalytics();
  const { farm } = useFarm();
  const { md } = Grid.useBreakpoint();

  return (
    <>
      <Breadcrumbs.components.Breadcrumb
        path={Routes.app.gamePlans.index}
        title={t<string>(Content.navigation.main.gamePlans)}
      />
      <section className={styles.PageContent}>
        <Switch>
          <Route
            path={_.values(routes.create)}
            render={({ location: { search } }) => {
              const searchParams = new URLSearchParams(search);
              const dataDescriptorID = searchParams.get("dataDescriptorID");

              return <CreateGamePlan farmID={farm ? +farm.id : null} dataDescriptorID={dataDescriptorID} />;
            }}
          />
          <Route
            path={_.values(routes.gamePlan.edit)}
            render={({
              match: {
                params: { gamePlanID }
              }
            }) => <EditGamePlan gamePlanID={gamePlanID as GamePlanEntity["id"]} />}
          />
          <Route
            path={routes.index}
            render={() => (
              <GamePlanContext.Provider value={{ layout: GamePlanVariant.Wide }}>
                <GAContext.Provider value={{ category: GACategories.gamePlanOverview }}>
                  <Onboarding />
                </GAContext.Provider>
                <GamePlanList
                  extra={[
                    <Link
                      key="create-game-plan"
                      to={routes.create.index}
                      onClick={() => {
                        /* istanbul ignore next */
                        trackEvent(GAActions.goToCreateGamePlan);
                      }}
                    >
                      <Button icon={<PlusCircleOutlined />} type="primary">
                        {t<string>(Content.gamePlan.actions.create, { variant: md ? "extended" : "short" })}
                      </Button>
                    </Link>
                  ]}
                />
              </GamePlanContext.Provider>
            )}
          />
        </Switch>
      </section>
    </>
  );
};

export default GamePlansPage;
